import React, { useEffect, useState, useMemo } from 'react';

import { calculateRowTotals } from '../../utils/calculations';
import { getBgClass, getIndentClass } from '../../utils/styleUtils';

import CommittedSection from './CommittedSection';
import ExpenseMilestoneData from './ExpenseMilestoneData';
import UncommittedSection from './UncommittedSection';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

export default function ExpenseRow(props) {
    const [milestonesCount, setMilestonesCount] = useState(null);
    const [showChildren, setShowChildren] = useState(false);

    // Calculate indentation levels
    const indentLevel = props.indentLevel || 0;
    const childIndentLevel = indentLevel + 1;

    useEffect(() => {
        setMilestonesCount(props.expense.currentMilestones.length);
    }, [props.expense]);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'rows' || t.batch === 'projects') {
            setShowChildren(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    const toggleShow = () => {
        setShowChildren(!showChildren);
    };

    const hasUncommittedData = useMemo(() => {
        return (
            props.expense.uncommitted &&
            (props.expense.uncommitted.value > 0 || (props.expense.uncommitted.currentMilestones && props.expense.uncommitted.currentMilestones.length > 0))
        );
    }, [props.expense.uncommitted]);

    const hasCommittedData = useMemo(() => {
        const committed = props.expense.committed;
        if (!committed) return false;

        if (Array.isArray(committed)) {
            return committed.length > 0 && committed.some((contract) => contract.currentMilestones && contract.currentMilestones.length > 0);
        } else if (typeof committed === 'object') {
            const contracts = Object.values(committed);
            return contracts.length > 0 && contracts.some((contract) => contract.currentMilestones && contract.currentMilestones.length > 0);
        }

        return false;
    }, [props.expense.committed]);

    const hasChildData = useMemo(() => {
        return hasUncommittedData || hasCommittedData;
    }, [hasUncommittedData, hasCommittedData]);

    const expenseValue = useMemo(() => {
        if (props.expense.value >= 0) {
            return formatFigure(props.expense.value, formatToFixedMoneyWithLocale, { precision: 2 });
        } else if (props.expense.cashflowObject) {
            return formatFigure(props.expense.cashflowObject.total, formatToFixedMoneyWithLocale, { precision: 2 });
        }
        return '';
    }, [props.expense.value, props.expense.cashflowObject]);

    const milestoneElements = useMemo(() => {
        if (!props.expense.currentMilestones) return null;

        return [...Array(props.maxExpenseMilestonesCount)].map((e, i) => (
            <React.Fragment key={`th_milestones_${i}_date_percentage`}>
                <ExpenseMilestoneData
                    key={`${props.keyPrefix || ''}tr_expense_${props.expense.id}_date_field_${i}`}
                    milestones={props.expense.currentMilestones}
                    milestoneId={i}
                    keyPrefix={props.keyPrefix || ''}
                    amountOnly={true}
                />
            </React.Fragment>
        ));
    }, [props.expense.currentMilestones, props.maxExpenseMilestonesCount, props.keyPrefix, props.expense.id]);

    const totals = useMemo(() => {
        return calculateRowTotals(props.expense.currentMilestones);
    }, [props.expense.currentMilestones]);

    return (
        <>
            {milestonesCount !== null && (
                <tr className={getBgClass(indentLevel)}>
                    <td className="!tw-text-charcoal-75">
                        <div
                            className={`tw-flex tw-items-center tw-gap-x-1.5 ${hasChildData ? 'tw-cursor-pointer' : ''} ${getIndentClass(indentLevel)}`}
                            onClick={hasChildData ? toggleShow : undefined}
                        >
                            <div className="tw-font-medium tw-text-blue">{props.expense.name}</div>
                            {hasChildData && <div>{showChildren ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>}
                        </div>
                    </td>
                    <td className="tw-p-4"></td>
                    <td className="tw-p-4 tw-text-right tw-border-r-2">{expenseValue}</td>
                    {milestoneElements}
                    <td className="tw-p-4 tw-text-right"></td>
                    <td className="tw-p-4 tw-text-right">{formatFigure(totals.totalValue, formatToFixedMoneyWithLocale, { precision: 2 })}</td>
                    <td className="tw-p-4 tw-text-center"></td>
                </tr>
            )}
            {showChildren && hasChildData && (
                <>
                    {hasUncommittedData && (
                        <UncommittedSection
                            {...props}
                            key={`uncommitted_expense_${props.expense.id}`}
                            expense={props.expense}
                            periods={props.periods}
                            maxExpenseMilestonesCount={props.maxExpenseMilestonesCount}
                            indentLevel={childIndentLevel}
                        />
                    )}
                    {hasCommittedData && (
                        <CommittedSection
                            {...props}
                            key={`committed_expense_${props.expense.id}`}
                            expense={props.expense}
                            periods={props.periods}
                            maxExpenseMilestonesCount={props.maxExpenseMilestonesCount}
                            indentLevel={childIndentLevel}
                        />
                    )}
                </>
            )}
        </>
    );
}
